import config from '../config';

/**
 * Service to interact with Google's Gemini API
 */
class GeminiService {
  constructor() {
    this.apiKey = config.geminiApiKey;
    this.apiEndpoint = config.apiEndpoints.geminiApi;
  }

  /**
   * Convert image to base64 string
   * @param {File} file - The image file to convert
   * @returns {Promise<string>} - Base64 encoded image
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract the base64 part of the data URL
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Extract questions from an image using Gemini API
   * @param {File} imageFile - The image file containing NEET questions
   * @returns {Promise<Array>} - Array of extracted questions
   */
  async extractQuestionsFromImage(imageFile) {
    try {
      const base64Image = await this.fileToBase64(imageFile);
      
      const response = await fetch(`${this.apiEndpoint}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: "Extract all NEET exam multiple choice questions from this image. Format them as a JSON array of question objects. Each question object should have the following properties: questionNumber (number), text (string), options (object with A, B, C, D keys and their values), subject (Biology, Chemistry, or Physics based on the content). Don't include the correct answer or solution."
                },
                {
                  inline_data: {
                    mime_type: imageFile.type,
                    data: base64Image
                  }
                }
              ]
            }
          ]
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to extract questions');
      }
      
      // Parse the response to extract questions
      const textContent = data.candidates[0].content.parts[0].text;
      
      // Try to extract JSON from the response
      let extractedQuestions = [];
      try {
        // Look for JSON within the text response
        const jsonMatch = textContent.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          extractedQuestions = JSON.parse(jsonMatch[0]);
        } else {
          // Fallback: parse the entire response as JSON
          extractedQuestions = JSON.parse(textContent);
        }
      } catch (parseError) {
        console.error("Error parsing JSON from Gemini response:", parseError);
        // Fallback: create a structured question from the text
        extractedQuestions = [{
          id: generateId(),
          questionNumber: 1,
          text: textContent.substring(0, 200) + "...",
          subject: guessSubjectFromText(textContent),
          options: {
            A: "Option A (extraction failed)",
            B: "Option B (extraction failed)",
            C: "Option C (extraction failed)",
            D: "Option D (extraction failed)"
          }
        }];
      }
      
      // Add IDs and image source to each question
      return extractedQuestions.map(q => ({
        ...q,
        id: generateId(),
        // Store both the URL for display and the original filename for reference
        imageSource: URL.createObjectURL(imageFile),
        sourceImageName: imageFile.name,
        solution: null,
        geminiAnswer: null
      }));
      
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      throw error;
    }
  }

  /**
   * Generate a solution for a NEET question using Gemini API
   * @param {Object} question - The question object
   * @returns {Promise<Object>} - Object containing solution text and correct answer
   */
  async generateSolution(question) {
    try {
      const prompt = `
You are an expert NEET exam tutor. Generate a detailed step-by-step solution for the following NEET ${question.subject} question.

Question: ${question.text}

Options:
A: ${question.options.A}
B: ${question.options.B}
C: ${question.options.C}
D: ${question.options.D}

Provide your response in the following format:

CORRECT_ANSWER: [LETTER]
(Just a single letter A, B, C, or D as the correct answer)

SOLUTION:
[Your step-by-step explanation here]

Make sure to state the correct answer clearly, show your work, and explain why the chosen option is correct and why others are incorrect if possible.
`;

      const response = await fetch(`${this.apiEndpoint}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: prompt
                }
              ]
            }
          ]
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to generate solution');
      }
      
      const responseText = data.candidates[0].content.parts[0].text;
      
      // Extract correct answer from the response
      const answerMatch = responseText.match(/CORRECT_ANSWER:\s*([ABCD])/i);
      const correctAnswer = answerMatch ? answerMatch[1].toUpperCase() : null;
      
      // Extract solution text (everything after "SOLUTION:")
      const solutionMatch = responseText.match(/SOLUTION:([\s\S]*)/i);
      const solutionText = solutionMatch ? solutionMatch[1].trim() : responseText;
      
      return {
        solution: solutionText,
        correctAnswer: correctAnswer
      };
      
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      throw error;
    }
  }

  /**
   * Generate a solution for a NEET question using Gemini Vision API and the full image
   * @param {Object} question - Question object
   * @param {File} imageFile - The image file containing the question paper
   * @returns {Promise<Object>} - Generated solution object with solution text and correct answer
   */
  async generateSolutionFromImage(question, imageFile) {
    try {
      const base64Image = await this.fileToBase64(imageFile);
      
      const prompt = `You are looking at a NEET exam paper image. Focus on question number ${question.questionNumber} and provide its solution.

Please provide your response in this format:
CORRECT_ANSWER: [LETTER]
(Just a single letter A, B, C, or D as the correct answer)

SOLUTION:
[Your step-by-step explanation here]`;

      const response = await fetch(`${this.apiEndpoint}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: prompt
                },
                {
                  inline_data: {
                    mime_type: imageFile.type,
                    data: base64Image
                  }
                }
              ]
            }
          ]
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to generate solution');
      }
      
      const responseText = data.candidates[0].content.parts[0].text;
      
      // Extract correct answer from the response
      const answerMatch = responseText.match(/CORRECT_ANSWER:\s*([ABCD])/i);
      const correctAnswer = answerMatch ? answerMatch[1].toUpperCase() : null;
      
      // Extract solution text (everything after "SOLUTION:")
      const solutionMatch = responseText.match(/SOLUTION:([\s\S]*)/i);
      const solutionText = solutionMatch ? solutionMatch[1].trim() : responseText;
      
      return {
        solution: solutionText,
        correctAnswer: correctAnswer
      };
      
    } catch (error) {
      console.error("Error calling Gemini Vision API:", error);
      throw error;
    }
  }

  /**
   * Generate solutions for multiple questions in batch
   * @param {Array} questions - Array of question objects
   * @returns {Promise<Array>} - Array of solution objects
   */
  async generateSolutions(questions) {
    const solutions = [];
    
    // Process questions sequentially to avoid rate limiting
    for (const question of questions) {
      try {
        const solutionData = await this.generateSolution(question);
        solutions.push({
          questionId: question.id,
          solution: solutionData.solution,
          correctAnswer: solutionData.correctAnswer
        });
      } catch (error) {
        console.error(`Error generating solution for question ${question.id}:`, error);
        // Add a placeholder for failed solutions
        solutions.push({
          questionId: question.id,
          solution: "Error generating solution. Please try again.",
          correctAnswer: null
        });
      }
      
      // Add a small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return solutions;
  }
}

// Helper function to generate a random ID
const generateId = () => Math.random().toString(36).substring(2, 9);

// Helper function to guess the subject from question text
const guessSubjectFromText = (text) => {
  const text_lower = text.toLowerCase();
  
  // Biology keywords
  const bioKeywords = ['cell', 'organism', 'species', 'dna', 'rna', 'protein', 'enzyme', 'mitochondria', 
                      'chloroplast', 'photosynthesis', 'respiration', 'chromosome', 'gene', 'allele', 
                      'inheritance', 'evolution', 'ecology', 'taxonomy', 'flora', 'fauna'];
  
  // Chemistry keywords
  const chemKeywords = ['atom', 'molecule', 'compound', 'element', 'reaction', 'acid', 'base', 'salt', 
                       'organic', 'inorganic', 'bond', 'electron', 'proton', 'neutron', 'periodic', 
                       'titration', 'equilibrium', 'solution', 'mole', 'stoichiometry'];
  
  // Physics keywords
  const physKeywords = ['force', 'energy', 'motion', 'velocity', 'acceleration', 'mass', 'weight', 
                       'gravity', 'electricity', 'magnetism', 'wave', 'optics', 'thermodynamics', 
                       'mechanics', 'circuit', 'current', 'resistance', 'voltage', 'quantum', 'relativity'];
  
  // Count matches for each subject
  let bioCount = bioKeywords.filter(word => text_lower.includes(word)).length;
  let chemCount = chemKeywords.filter(word => text_lower.includes(word)).length;
  let physCount = physKeywords.filter(word => text_lower.includes(word)).length;
  
  // Return the subject with the most keyword matches
  if (bioCount >= chemCount && bioCount >= physCount) return 'Biology';
  if (chemCount >= bioCount && chemCount >= physCount) return 'Chemistry';
  return 'Physics';
};

// Export a singleton instance
export const geminiService = new GeminiService();